/** Connexion, inscription d'un candidat formateur (F-ONB-01), acceptation d'une invitation d'apprenant (F-COM-05). */
import { useState, type ReactNode } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ErreurApi } from "../api";
import { api } from "../api";
import { ETAPES } from "@/domaine/candidature/questionnaire";
import { Alerte, Bouton, Champ, Chargement, cx, useTitreDocument } from "../ui/base";
import { LiensLegaux, Marque, useIdentite } from "../ui/marque";
import { lienConfidentialite } from "../liens-legaux";

type Illustration = { src: string; alt: string; message: string; suite?: ReactNode };

export function Coquille({ titre, accroche, children, pied, illustration }: { titre: string; accroche: string; children: ReactNode; pied?: ReactNode; illustration?: Illustration }) {
  useTitreDocument(titre);
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      {illustration ? (
        // Candidature : l'illustration du premier SaaS, plein cadre, avec ce qui attend le candidat.
        <aside className="relative hidden overflow-hidden bg-encre text-sur-accent lg:block">
          <img src={illustration.src} alt={illustration.alt} width={720} height={960} className="absolute inset-0 size-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-encre/95 via-encre/40 to-encre/10" />
          <div className="relative flex h-full flex-col justify-between p-12">
            <div>
              <Marque taille="h-8" sombre />
            </div>
            <div>
              <p className="max-w-[20ch] font-display text-[36px] leading-[1.12] font-semibold">{illustration.message}</p>
              {illustration.suite}
            </div>
          </div>
        </aside>
      ) : (
      <aside className="hidden flex-col justify-between bg-encre p-12 text-sur-accent lg:flex">
        <div>
          <Marque taille="h-8" sombre />
          <p className="mt-2 text-sm font-medium text-sur-accent/70">Plateforme des dossiers de formation</p>
        </div>
        <div>
          <p className="max-w-[22ch] font-display text-[40px] leading-[1.1] font-semibold">Un dossier complet, sans courir après les signatures.</p>
          <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-sur-accent/70">
            De la candidature du formateur à l'archivage : chaque pièce est générée, suivie, signée et classée — conforme au référentiel Qualiopi.
          </p>
        </div>
        {/* Frise des sept étapes : un vrai espace entre les libellés (« Financement » et « Début » se touchaient). */}
        <ol className="grid grid-cols-7 gap-x-3 text-[11px] font-medium text-sur-accent/60" aria-label="Les sept étapes d'un dossier">
          {["Création", "Financement", "Début", "Fin", "Paiement", "Encaissé", "Archivé"].map((e, i) => (
            <li key={e} className="min-w-0">
              <span className={`mb-2 block h-1 rounded-full ${i < 3 ? "bg-attente" : "bg-sur-accent/20"}`} />
              <span className="block truncate" title={e}>
                {e}
              </span>
            </li>
          ))}
        </ol>
      </aside>
      )}
      {/* Téléphone : contenu calé en haut sous un en-tête de marque compact (centré verticalement, il laissait un grand vide). */}
      <main className="flex items-start justify-center px-5 pt-8 pb-12 sm:items-center sm:py-12">
        <div className="w-full max-w-sm">
          <div className={cx("lg:hidden", illustration ? "mb-6" : "mb-10")}>
            <Marque taille="h-6" />
            <p className="mt-1.5 text-[13px] font-medium text-encre-2">Plateforme des dossiers de formation</p>
            {illustration && <img src={illustration.src} alt="" width={720} height={960} className="mt-5 h-32 w-full rounded-md object-cover object-[center_30%]" />}
          </div>
          <h1 className="text-[28px] font-semibold leading-tight">{titre}</h1>
          <p className="mt-2 text-encre-2">{accroche}</p>
          <div className="mt-8">{children}</div>
          {pied && <div className="mt-8 border-t border-trait pt-5 text-sm text-encre-2">{pied}</div>}
          <LiensLegaux className="mt-10" />
        </div>
      </main>
    </div>
  );
}

export function Connexion() {
  const [email, setEmail] = useState("");
  const [mdp, setMdp] = useState("");
  const [code2fa, setCode2fa] = useState("");
  const [deuxFacteurs, setDeuxFacteurs] = useState(false);
  const requetes = useQueryClient();
  const connexion = useMutation({
    mutationFn: () => api.post("/auth/connexion", { email, mot_de_passe: mdp, ...(deuxFacteurs ? { code_2fa: code2fa } : {}) }),
    onError: (e) => {
      // Double authentification active sur ce compte : on demande le code de l'application d'authentification.
      if ((e as ErreurApi).details && "code_2fa_requis" in ((e as ErreurApi).details as object)) setDeuxFacteurs(true);
    },
    onSuccess: () => {
      requetes.clear();
      // Rechargement complet volontaire : la session change, tout l'état en mémoire doit repartir de zéro.
      const retour = new URLSearchParams(location.search).get("retour");
      location.assign(retour?.startsWith("/") && !retour.startsWith("//") ? retour : "/");
    },
  });
  return (
    <Coquille
      titre="Connexion"
      accroche="Formateur, apprenant ou organisme : un seul accès."
      pied={
        <>
          Vous êtes formateur et souhaitez être porté ?{" "}
          <Link to="/inscription" className="font-medium text-accent underline-offset-4 hover:underline">
            Déposer une candidature
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); connexion.mutate(); }}>
        <Champ libelle="Adresse e-mail" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Champ libelle="Mot de passe" type="password" autoComplete="current-password" required value={mdp} onChange={(e) => setMdp(e.target.value)} />
        {deuxFacteurs && (
          <Champ libelle="Code de double authentification" inputMode="numeric" autoComplete="one-time-code" maxLength={7} required autoFocus aide="Six chiffres affichés par votre application d'authentification." value={code2fa} onChange={(e) => setCode2fa(e.target.value)} />
        )}
        {connexion.error && <Alerte ton="danger">{connexion.error.message}</Alerte>}
        <Bouton type="submit" variante="primaire" className="w-full" enCours={connexion.isPending}>
          Se connecter
        </Bouton>
        <p className="text-center text-sm">
          <Link to="/mot-de-passe-oublie" className="text-accent underline-offset-4 hover:underline">
            Mot de passe oublié ?
          </Link>
        </p>
      </form>
    </Coquille>
  );
}

export function Inscription() {
  const [v, setV] = useState({ prenom: "", nom: "", email: "", mot_de_passe: "" });
  // Programme ambassadeur : code de parrainage reçu dans l'adresse (?parrain=CODE), transmis tel quel ; le serveur l'ignore s'il est inconnu.
  const { parrain } = useSearch({ from: "/inscription" });
  const codeParrain = typeof parrain === "string" && /^[A-Za-z0-9]{8}$/.test(parrain) ? parrain.toUpperCase() : "";
  const confidentialite = lienConfidentialite(useIdentite().data);
  const champ = (cle: keyof typeof v) => ({ value: v[cle], onChange: (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [cle]: e.target.value }) });
  const inscription = useMutation({ mutationFn: () => api.post("/auth/inscription", codeParrain ? { ...v, parrain: codeParrain } : v), onSuccess: () => location.assign("/candidature") });
  const longueurOk = v.mot_de_passe.length >= 10;
  return (
    <Coquille
      titre="Candidature formateur"
      accroche="Créez votre accès : votre candidature s'enregistre ensuite étape par étape, vous la reprenez quand vous voulez."
      illustration={{
        src: "/candidature/etape-8.webp",
        alt: "Formatrice se connectant à son espace en ligne depuis son ordinateur",
        message: "Vous formez. Nous portons l'administratif et la qualité.",
        suite: (
          <>
            <p className="mt-6 text-[13px] font-medium tracking-wide text-sur-accent/75 uppercase">Ensuite, huit étapes courtes</p>
            <ol className="mt-3 grid max-w-md grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-sur-accent/90">
              {ETAPES.map((e, i) => (
                <li key={e.id} className="flex items-center gap-2">
                  <span className="inline-flex size-5 items-center justify-center rounded-full border border-sur-accent/60 text-[10.5px] font-semibold">{i + 1}</span>
                  {e.titre}
                </li>
              ))}
            </ol>
          </>
        ),
      }}
      pied={
        <>
          Déjà inscrit ?{" "}
          <Link to="/connexion" className="font-medium text-accent underline-offset-4 hover:underline">
            Se connecter
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); inscription.mutate(); }}>
        {codeParrain && (
          <Alerte ton="succes" titre="Vous êtes parrainé·e">
            Un formateur du réseau vous a recommandé la plateforme (code {codeParrain}). Ce parrainage sera rattaché à votre compte.
          </Alerte>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Prénom" autoComplete="given-name" required {...champ("prenom")} />
          <Champ libelle="Nom" autoComplete="family-name" required {...champ("nom")} />
        </div>
        <Champ libelle="Adresse e-mail" type="email" autoComplete="email" required {...champ("email")} />
        <Champ libelle="Mot de passe" type="password" autoComplete="new-password" required minLength={10} aide={longueurOk ? "✓ Dix caractères au minimum : c'est bon." : "Dix caractères au minimum."} {...champ("mot_de_passe")} />
        {inscription.error && <Alerte ton="danger">{inscription.error.message}</Alerte>}
        <Bouton type="submit" variante="primaire" className="w-full" enCours={inscription.isPending}>
          Créer mon accès et commencer
        </Bouton>
        <p className="text-[12.5px] leading-relaxed text-encre-3">
          Vos données servent uniquement à l'étude de votre candidature et à votre espace formateur.
          {confidentialite && (
            <>
              {" "}
              <a href={confidentialite} target="_blank" rel="noopener noreferrer" className="font-medium text-accent underline underline-offset-2">
                Politique de confidentialité<span className="sr-only"> (nouvel onglet)</span>
              </a>
            </>
          )}
        </p>
      </form>
    </Coquille>
  );
}

export function Invitation() {
  const { jeton } = useParams({ from: "/invitation/$jeton" });
  const [mdp, setMdp] = useState("");
  const invitation = useQuery({ queryKey: ["invitation", jeton], queryFn: () => api.get<{ email: string; prenom: string }>(`/auth/invitation/${jeton}`), retry: false });
  const accepter = useMutation({ mutationFn: () => api.post(`/auth/invitation/${jeton}`, { mot_de_passe: mdp }), onSuccess: () => location.assign("/") });

  if (invitation.isPending) return <Coquille titre="Votre espace personnel" accroche=""><Chargement /></Coquille>;
  if (invitation.error) {
    return (
      <Coquille titre="Lien expiré" accroche="Ce lien d'invitation n'est plus valable.">
        <Alerte ton="attention">{(invitation.error as ErreurApi).message}</Alerte>
        <p className="mt-5 text-sm text-encre-2">
          Vous avez déjà choisi un mot de passe ?{" "}
          <Link to="/connexion" className="font-medium text-accent underline-offset-4 hover:underline">
            Connectez-vous
          </Link>
          .
        </p>
      </Coquille>
    );
  }
  return (
    <Coquille titre={`Bienvenue, ${invitation.data.prenom}`} accroche="Choisissez un mot de passe pour accéder à vos documents de formation.">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); accepter.mutate(); }}>
        <Champ libelle="Adresse e-mail" value={invitation.data.email} disabled readOnly />
        <Champ libelle="Votre mot de passe" type="password" autoComplete="new-password" required minLength={10} aide="Dix caractères au minimum." value={mdp} onChange={(e) => setMdp(e.target.value)} />
        {accepter.error && <Alerte ton="danger">{accepter.error.message}</Alerte>}
        <Bouton type="submit" variante="primaire" className="w-full" enCours={accepter.isPending}>
          Accéder à mon espace
        </Bouton>
      </form>
    </Coquille>
  );
}

/** « Mot de passe oublié » : la réponse est la même que l'adresse existe ou non (on ne révèle pas les comptes). */
export function MotDePasseOublie() {
  const [email, setEmail] = useState("");
  const demande = useMutation({ mutationFn: () => api.post<{ message: string }>("/auth/mot-de-passe-oublie", { email }) });
  return (
    <Coquille titre="Mot de passe oublié" accroche="Indiquez l'adresse de votre compte : vous recevrez un lien pour choisir un nouveau mot de passe." pied={<Link to="/connexion" className="font-medium text-accent underline-offset-4 hover:underline">Revenir à la connexion</Link>}>
      {demande.isSuccess ? (
        <Alerte ton="succes">{demande.data.message}</Alerte>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); demande.mutate(); }}>
          <Champ libelle="Adresse e-mail" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          {demande.error && <Alerte ton="danger">{demande.error.message}</Alerte>}
          <Bouton type="submit" variante="primaire" className="w-full" enCours={demande.isPending}>Recevoir le lien</Bouton>
        </form>
      )}
    </Coquille>
  );
}

export function Reinitialisation() {
  const { jeton } = useParams({ from: "/reinitialisation/$jeton" });
  const [mdp, setMdp] = useState("");
  const lien = useQuery({ queryKey: ["reinitialisation", jeton], queryFn: () => api.get<{ email_masque: string }>(`/auth/reinitialisation/${jeton}`), retry: false });
  const changer = useMutation({ mutationFn: () => api.post(`/auth/reinitialisation/${jeton}`, { mot_de_passe: mdp }) });
  if (lien.isPending) return <Coquille titre="Nouveau mot de passe" accroche=""><Chargement /></Coquille>;
  if (lien.error) {
    return (
      <Coquille titre="Lien expiré" accroche="Ce lien n'est plus valable.">
        <Alerte ton="attention">{(lien.error as ErreurApi).message}</Alerte>
        <p className="mt-5 text-sm"><Link to="/mot-de-passe-oublie" className="font-medium text-accent underline-offset-4 hover:underline">Faire une nouvelle demande</Link></p>
      </Coquille>
    );
  }
  return (
    <Coquille titre="Nouveau mot de passe" accroche={`Compte ${lien.data.email_masque}. Toutes les sessions ouvertes seront fermées.`}>
      {changer.isSuccess ? (
        <>
          <Alerte ton="succes">Votre mot de passe est changé.</Alerte>
          <p className="mt-5 text-sm"><Link to="/connexion" className="font-medium text-accent underline-offset-4 hover:underline">Se connecter</Link></p>
        </>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); changer.mutate(); }}>
          <Champ libelle="Nouveau mot de passe" type="password" autoComplete="new-password" required minLength={10} aide="Dix caractères au minimum." value={mdp} onChange={(e) => setMdp(e.target.value)} />
          {changer.error && <Alerte ton="danger">{changer.error.message}</Alerte>}
          <Bouton type="submit" variante="primaire" className="w-full" enCours={changer.isPending}>Enregistrer</Bouton>
        </form>
      )}
    </Coquille>
  );
}
